import { queryClient } from '@/fsd/shared/lib/react-query/queryClient';
import { makeAutoObservable } from 'mobx';
import { Step } from 'react-joyride';
import { NavigateFunction } from 'react-router-dom';

import { accountCabinetOnboardingProgress } from '../../api/accountCabinetOnboardingProgress/accountCabinetOnboardingProgress';
import { accountCabinetOnboardingStart } from '../../api/accountCabinetOnboardingStart/accountCabinetOnboardingStart';
import { getAccountCabinetOnboardingState } from '../../api/getAccountCabinetOnboardingState/getAccountCabinetOnboardingState';
import {
  clearOnboardingStepKey,
  getOnboardingStepKey,
  LOCAL_STORAGE_ONBOARDING_SHOW_KEY,
  setOnboardingStepKey,
} from '../consts/consts';
import { tourSteps } from '../consts/tourSteps';
import {
  IGetAccountCabinetOnboardingStateResultOne,
  IGetAccountCabinetOnboardingStateResultTwo,
} from '../types/IGetAccountCabinetOnboardingStateResult';
import { IStep } from '../types/IStep';
import { TourStepId } from '../types/TourStepId';
import { getStepTarget } from '@fsd/entities/onboarding/lib/getStepTarget';
import { TOUR_STEP_IDS } from '@fsd/entities/onboarding/model/consts/tourStepIds';

class OnboardingStore {
  show = false;
  run = false;
  currentStepIndex = 0;
  activeTourSteps: Step[] = [];
  navigate?: NavigateFunction;
  tourSteps: Step[] = [];
  cashboxId: number = parseInt(localStorage.getItem('cashboxId') ?? '0');

  constructor() {
    makeAutoObservable(this);
  }

  setNavigate(navigate: NavigateFunction) {
    this.navigate = navigate;
    this.tourSteps = tourSteps(navigate);
  }

  setRun(run: boolean) {
    this.run = run;
  }

  setCashboxId(id: number) {
    this.cashboxId = id;
    localStorage.setItem('cashboxId', `${id}`);
  }

  getCurrentStep(): Step | undefined {
    return this.activeTourSteps[this.currentStepIndex];
  }

  getPrevStep(): Step | undefined {
    if (this.currentStepIndex > 0)
      return this.activeTourSteps[this.currentStepIndex - 1];
  }

  getStoredStepKey(): string | null {
    return getOnboardingStepKey();
  }

  persistCurrentStep(stepKey?: string) {
    setOnboardingStepKey(stepKey);
  }

  async jumpToStep(id: TourStepId | string) {
    const index = this.activeTourSteps.findIndex((step) => step.id === id);

    const targetStep = this.activeTourSteps[index];

    this.setRun(false);
    this.setStepIndex(index);
    await Promise.resolve();
    const ready = await this.waitForStepTarget(targetStep);
    if (ready) {
      this.setRun(true);
    }
  }

  isStepReady(step?: Step): boolean {
    if (!step) {
      return true;
    }

    const target = step.target;
    if (!target || target === 'body') {
      return true;
    }

    const selector =
      String(target) ===
      `${getStepTarget(TOUR_STEP_IDS.DATE_ROW_CALENDAR)} ${getStepTarget(
        TOUR_STEP_IDS.MONTH_PICKER
      )}`
        ? String(getStepTarget(TOUR_STEP_IDS.DATE_ROW_CALENDAR))
        : String(target);
    const element = document.querySelector(selector);
    if (!element) {
      return false;
    }

    const rect = (element as HTMLElement).getBoundingClientRect?.();
    if (!rect) {
      return true;
    }

    return rect.width > 0 || rect.height > 0;
  }

  async waitForStepTarget(
    step?: Step,
    timeoutMs = 15000,
    intervalMs = 150
  ): Promise<boolean> {
    if (!step) {
      return true;
    }

    if (this.isStepReady(step)) {
      return true;
    }

    const startedAt = Date.now();

    return await new Promise((resolve) => {
      const check = () => {
        if (this.isStepReady(step)) {
          resolve(true);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          resolve(false);
          return;
        }

        setTimeout(check, intervalMs);
      };

      check();
    });
  }

  async ensureCurrentStepTargetReady(): Promise<boolean> {
    const step = this.getCurrentStep();

    if (!step) {
      this.setRun(true);
      return true;
    }

    if (this.isStepReady(step)) {
      this.setRun(true);
      return true;
    }

    this.setRun(false);
    const ready = await this.waitForStepTarget(step);

    if (ready) {
      this.setRun(true);
    }

    return ready;
  }

  async initStore() {
    const data = await queryClient.fetchQuery({
      queryKey: ['onboarding-state'],
      queryFn: getAccountCabinetOnboardingState,
    });
    localStorage.removeItem('clicked');

    if (data.status === 'completed') {
      this.setRun(false);
      this.setShow(false);
      clearOnboardingStepKey();
      return;
    }

    if (this.isOnboardingActive(data)) {
      const orderedSteps = this.buildOrderedSteps(data.steps);
      this.setActiveTourSteps(orderedSteps);

      if (data.status === 'not_started') {
        // await accountCabinetOnboardingStart();
        // const nextStep = orderedSteps[0];
        // this.setStepIndex(0);
        // if (nextStep?.id) {
        //   this.persistCurrentStep(nextStep.id);
        // }
        // this.run = false;
        this.setShow(true);
        return;
      }

      if (data.status === 'in_progress') {
        const storedStepKey = this.getStoredStepKey() || data.current_step_key;
        const initialStepKey =
          storedStepKey || this.getFirstStepId(orderedSteps);
        const initialIndex = this.resolveCurrentStepIndex(initialStepKey);

        this.setStepIndex(initialIndex);

        const syncedStepKey = this.getCurrentStep()?.id || initialStepKey;
        if (syncedStepKey) {
          this.persistCurrentStep(syncedStepKey);
          const { current_step_key } = await accountCabinetOnboardingProgress({
            step_key: syncedStepKey,
          });
          const index = this.resolveCurrentStepIndex(current_step_key);
          this.setStepIndex(index);
        }

        this.setRun(true);
        return;
      }
    }

    if (data.active) {
      this.setShow(true);
    }
  }

  private getFirstStepId(steps: Step[]): string | undefined {
    return steps.find((step) => typeof step.id === 'string')?.id;
  }

  private isOnboardingActive(
    data:
      | IGetAccountCabinetOnboardingStateResultOne
      | IGetAccountCabinetOnboardingStateResultTwo
  ): data is IGetAccountCabinetOnboardingStateResultOne {
    return 'steps' in data && 'current_step_key' in data;
  }

  private groupFrontendSteps(): Record<string, Step> {
    const grouped: Record<string, Step> = {};
    this.tourSteps.forEach((step) => {
      if (step.id) {
        grouped[step.id] = step;
      }
    });
    return grouped;
  }

  private buildOrderedSteps(backendSteps: IStep[]): Step[] {
    const groupedFrontend = this.groupFrontendSteps();
    const sortedBackendSteps = [...backendSteps].sort(
      (a, b) => a.order - b.order
    );
    const orderedSteps: Step[] = [];

    sortedBackendSteps.forEach((backendStep) => {
      const correspondingFrontEndStep = groupedFrontend[backendStep.key];
      if (correspondingFrontEndStep) {
        orderedSteps.push({
          ...correspondingFrontEndStep,
          skipBeacon: true,
          content: (
            <div dangerouslySetInnerHTML={{ __html: backendStep.text }} />
          ),
        });
      }
    });

    return orderedSteps;
  }

  private resolveCurrentStepIndex(currentStepKey?: string): number {
    if (!currentStepKey) {
      return 0;
    }

    const index = this.activeTourSteps.findIndex(
      (step) => step.id === currentStepKey
    );
    return index !== -1 ? index : 0;
  }

  resumeFromSavedStep() {
    const savedStepKey = this.getStoredStepKey();
    if (savedStepKey) {
      this.setStepIndex(this.resolveCurrentStepIndex(savedStepKey));
      return;
    }

    this.setStepIndex(0);
  }

  async startTour() {
    await accountCabinetOnboardingStart();
    const nextStep = this.activeTourSteps[0];
    if (nextStep?.id) {
      this.persistCurrentStep(nextStep.id);
    }
    this.resumeFromSavedStep();
    this.setRun(true);
    // this.setShow(true);
    // localStorage.removeItem('clicked');
  }

  setActiveTourSteps(steps: Step[]) {
    this.activeTourSteps = steps;
  }

  stopTour() {
    this.setRun(false);
    this.setShow(false);
    // localStorage.removeItem('clicked');
  }

  setStepById(id: TourStepId) {
    const index = this.activeTourSteps.findIndex((step) => step.id === id);
    if (index !== -1) {
      this.setStepIndex(index);
    }
  }

  setShow(newShow: boolean) {
    this.show = newShow;
    localStorage.setItem(LOCAL_STORAGE_ONBOARDING_SHOW_KEY, String(newShow));
    // localStorage.removeItem('clicked');
  }

  setStepIndex(index: number) {
    if (index < 0) {
      this.currentStepIndex = 0;
      return;
    }

    const safeIndex =
      this.activeTourSteps.length > 0
        ? Math.min(index, this.activeTourSteps.length - 1)
        : 0;

    this.currentStepIndex = safeIndex;

    const currentStep = this.activeTourSteps[safeIndex];
    if (currentStep?.id) {
      this.persistCurrentStep(currentStep.id);
    }
  }
}

export const onboardingStore = new OnboardingStore();
